import React, { useEffect, useState } from 'react'
import { Button } from 'antd'
import { PlayCircleOutlined } from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import qs from 'qs'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import {
  getOnboardingV2Progress,
  shouldShowOnboardingV2Progress,
  initializeOnboardingV2Progress,
  OnboardingV2Progress,
  ONBOARDING_V2_PROGRESS_EVENT
} from '../../util/onboarding-v2-storage'
import { showOnboardingV2Modal } from '../onboarding-v2-modal'

import { App } from 'antd'
import { HeatmapData } from '../../routes/DashboardPage/types'
import IntlMessages from '../../util/IntlMessages'

import { IUserInfo } from '../../types/users'
import { ILocale } from '@vacationtracker/shared/types/i18n'

interface OnboardingV2ProgressButtonProps {
  authUser: IUserInfo
  locale: ILocale
  heatmapData: HeatmapData
}

export const OnboardingV2ProgressButton: React.FC<OnboardingV2ProgressButtonProps> = ({
  authUser,
  locale,
  heatmapData,
}) => {
  const { modal } = App.useApp()
  const location = useLocation()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const [progress, setProgress] = useState<OnboardingV2Progress | null>(null)
  const [shouldShow, setShouldShow] = useState(false)
  const completed = progress?.completedSteps.length || 0
  const total = progress?.totalSteps || 3 // 3 main setup steps

  // Parse URL parameters to check if onboardingV2 is enabled
  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
  const hasOnboardingV2Param = queryParams.onboardingV2 === 'true'

  useEffect(() => {
    // Only show onboarding progress if user has the onboardingV2 URL parameter

    if (authUser?.id && authCompany?.id) {
      const storedProgress = getOnboardingV2Progress(authUser.id, authCompany.id)
      setProgress(storedProgress)
      setShouldShow(shouldShowOnboardingV2Progress(storedProgress) || authUser?.initialUser)

      // React to local progress updates without needing to reopen modal
      const handleProgressUpdated = () => {
        const updated = getOnboardingV2Progress(authUser.id, authCompany.id)
        setProgress(updated)
        setShouldShow(shouldShowOnboardingV2Progress(updated) || authUser?.initialUser)
      }
      window.addEventListener(ONBOARDING_V2_PROGRESS_EVENT, handleProgressUpdated as EventListener)
      return () => window.removeEventListener(ONBOARDING_V2_PROGRESS_EVENT, handleProgressUpdated as EventListener)
    }
  }, [authUser?.id, authCompany?.id, hasOnboardingV2Param])

  const handleClick = () => {
    if (authUser && locale && heatmapData) {
      // Initialize progress if it doesn't exist
      if (!progress && authUser?.id && authCompany?.id) {
        initializeOnboardingV2Progress(authUser.id, authCompany.id, 3) // 3 main setup steps
      }

      showOnboardingV2Modal(modal, authUser, locale, heatmapData, {
        onClose: () => {
          // Refresh progress after modal closes
          if (authUser?.id && authCompany?.id) {
            const updatedProgress = getOnboardingV2Progress(authUser.id, authCompany.id)
            setProgress(updatedProgress)
            setShouldShow(shouldShowOnboardingV2Progress(updatedProgress))
          }
        },
        onExploreFirst: () => {
          // Refresh progress after modal closes
          if (authUser?.id && authCompany?.id) {
            const updatedProgress = getOnboardingV2Progress(authUser.id, authCompany.id)
            setProgress(updatedProgress)
            setShouldShow(shouldShowOnboardingV2Progress(updatedProgress))
          }
        },
        onMinimize: () => {
          // Refresh progress after modal closes
          if (authUser?.id && authCompany?.id) {
            const updatedProgress = getOnboardingV2Progress(authUser.id, authCompany.id)
            setProgress(updatedProgress)
            setShouldShow(shouldShowOnboardingV2Progress(updatedProgress))
          }
        },
      })
    }
  }

  // Only show button if user has onboardingV2 URL parameter AND either has progress or is initial user
  if (!shouldShow) {
    return null
  }

  return (
    <Button
      type="primary"
      icon={<PlayCircleOutlined />}
      onClick={handleClick}
      size="small"
      style={{ color: 'white' }}
    >
      <IntlMessages id="onboarding.v2.progress.button" values={{ completed, total }} />
    </Button>
  )
}
